import React from 'react';
import { useTranslation } from 'react-i18next';

import type { PriceList } from '../../services/types';

const MACHINE_TYPES = ['SIDEL', 'KRONES', 'KHS', 'N/A'];
const MATCH_TYPES = [
  'EXACT_MATCH',
  'SUFFIX_MATCH',
  'REVISION_MATCH',
  'NOTE_MATCH',
  'DESCRIPTION_MATCH',
];

const INPUT_CLASS =
  'w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent';

/** What is typed in the text filters, until a pause turns it into the filter itself */
export interface TypedFilters {
  part: string;
  user: string;
  file: string;
  q: string;
}

interface ListParserDataFiltersProps {
  filters: {
    from: string;
    to: string;
    run: string;
    machineType: string;
    matchType: string;
    priceList: string;
    matched: string;
  };
  typed: TypedFilters;
  onType: (
    field: keyof TypedFilters
  ) => (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFilter: (changes: Record<string, string>) => void;
  priceLists: PriceList[];
}

/** The filters of the List Parser Data page */
export const ListParserDataFilters: React.FC<ListParserDataFiltersProps> = ({
  filters,
  typed,
  onType,
  onFilter,
  priceLists,
}) => {
  const { t } = useTranslation();

  return (
    <>
      <div className="mb-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <label className="text-sm text-gray-700">
          {t('listParserData.from')}
          <input
            type="date"
            value={filters.from}
            max={filters.to}
            disabled={Boolean(filters.run)}
            onChange={(e) => onFilter({ from: e.target.value })}
            className={INPUT_CLASS}
          />
        </label>
        <label className="text-sm text-gray-700">
          {t('listParserData.to')}
          <input
            type="date"
            value={filters.to}
            min={filters.from}
            disabled={Boolean(filters.run)}
            onChange={(e) => onFilter({ to: e.target.value })}
            className={INPUT_CLASS}
          />
        </label>
        <label className="text-sm text-gray-700">
          {t('listParserData.partNumber')}
          <input
            type="text"
            value={typed.part}
            onChange={onType('part')}
            className={INPUT_CLASS}
          />
        </label>
        <label className="text-sm text-gray-700">
          {t('listParserData.user')}
          <input
            type="text"
            value={typed.user}
            placeholder={t('listParserData.userPlaceholder')}
            onChange={onType('user')}
            className={INPUT_CLASS}
          />
        </label>
        <label className="text-sm text-gray-700">
          {t('listParserData.fileName')}
          <input
            type="text"
            value={typed.file}
            onChange={onType('file')}
            className={INPUT_CLASS}
          />
        </label>
        <label className="text-sm text-gray-700">
          {t('listParserData.machineType')}
          <select
            value={filters.machineType}
            onChange={(e) => onFilter({ machine: e.target.value })}
            className={INPUT_CLASS}
          >
            <option value="">{t('listParserData.allMachineTypes')}</option>
            {MACHINE_TYPES.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm text-gray-700">
          {t('listParserData.columns.matchType')}
          <select
            value={filters.matchType}
            onChange={(e) => onFilter({ matchType: e.target.value })}
            className={INPUT_CLASS}
          >
            <option value="">{t('listParserData.allMatchTypes')}</option>
            {MATCH_TYPES.map((type) => (
              <option key={type} value={type}>
                {t(`listParserData.matchTypes.${type}`)}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm text-gray-700">
          {t('listParserData.priceList')}
          <select
            value={filters.priceList}
            onChange={(e) => onFilter({ priceList: e.target.value })}
            className={INPUT_CLASS}
          >
            <option value="">{t('listParserData.allPriceLists')}</option>
            {priceLists.map((list) => (
              <option key={list.id} value={list.id}>
                {list.id}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm text-gray-700">
          {t('listParserData.matched')}
          <select
            value={filters.matched}
            onChange={(e) => onFilter({ matched: e.target.value })}
            className={INPUT_CLASS}
          >
            <option value="">{t('listParserData.allRows')}</option>
            <option value="true">{t('listParserData.matchedOnly')}</option>
            <option value="false">{t('listParserData.unmatchedOnly')}</option>
          </select>
        </label>
        <label className="text-sm text-gray-700">
          {t('listParserData.search')}
          <input
            type="text"
            value={typed.q}
            placeholder={t('listParserData.searchPlaceholder')}
            onChange={onType('q')}
            className={INPUT_CLASS}
          />
        </label>
      </div>
      <p className="mb-4 text-xs text-gray-500">{t('listParserData.hint')}</p>
    </>
  );
};
